/**
 * WatcherPickerModal
 *
 * Dialog for managing task watchers. Candidates are the clinicians assigned
 * to the task's client (not the whole org), excluding the assignee (already
 * follows the task) and the current user (can't watch their own task). Loads
 * once per open, pre-checks current watchers, and diffs the selection on
 * Save to fire only the necessary add/remove mutations.
 *
 * The inner WatcherPickerContent is mounted fresh on each open (via key prop),
 * so its state initialises cleanly from currentWatchers without a useEffect.
 */

import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../ui/Modal/Modal';
import { Button } from '../ui/Button/Button';
import { Avatar } from '../ui/Avatar/Avatar';
import { useAddWatcher, useRemoveWatcher } from '../../hooks/useTasks';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import clientService from '../../services/api/clientService';
import { formatPersonMeta, matchesPerson } from '../shared/PersonMeta';
import { useAuthStore } from '../../stores/authStore';
import { theme } from '../../styles/theme';
import type { TaskWatcher } from '../../types/tasks';

// Stack above the TaskDetailPanel overlay (theme.zIndex.modal + 10) so the picker
// is visible when opened from inside a task detail panel (e.g. via My Tasks).
const WATCHER_PICKER_Z_INDEX = theme.zIndex.modal + 20;

// ============================================================================
// Props
// ============================================================================

export interface WatcherPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  /** The client the task belongs to — candidates are this client's assigned clinicians. */
  clientId?: string | null;
  currentWatchers: TaskWatcher[];
  /**
   * The task's assignee — excluded from the selectable list since the assignee
   * already follows the task by virtue of being assigned (they cannot be a watcher).
   */
  assigneeUserId?: string | null;
}

// ============================================================================
// Styled components
// ============================================================================

const SearchBox = styled.div`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  svg {
    position: absolute;
    left: ${({ theme }) => theme.spacing[3]};
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.text.light};
    pointer-events: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  padding-left: ${({ theme }) => theme.spacing[8]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.light};
  }
`;

const MemberList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const MemberRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  /* min-height (not height) so a wrapped "Location · Role" line can grow the row. */
  min-height: 44px;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[1]}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }
`;

const MemberCheckbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${({ theme }) => theme.colors.primary.base};
  cursor: pointer;
  flex-shrink: 0;
`;

const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const MemberName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MemberMeta = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  overflow-wrap: anywhere;
`;

const StatusText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.light};
  margin: 0;
  padding: ${({ theme }) => theme.spacing[4]} 0;
  text-align: center;
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: ${({ theme }) => `${theme.spacing[2]} 0 0`};
`;

/**
 * FooterRow fills the Modal's flex footer and positions the error message on
 * the left while pushing the action buttons to the right via margin-left: auto
 * on the spacer element.
 */
const FooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex: 1;
`;

/** Pushes trailing buttons to the right end of FooterRow */
const FooterSpacer = styled.span`
  flex: 1;
`;

// ============================================================================
// Inner component — mounted fresh on each modal open via key prop
// ============================================================================

interface ContentProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string;
  clientId?: string | null;
  currentWatchers: TaskWatcher[];
  assigneeUserId?: string | null;
}

function WatcherPickerContent({
  isOpen,
  onClose,
  taskId,
  clientId,
  currentWatchers,
  assigneeUserId,
}: ContentProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  // Initialise selection from currentWatchers on mount (key prop ensures fresh mount on open)
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(currentWatchers.map((w) => w.userId)),
  );
  const [saveError, setSaveError] = useState<string | null>(null);

  const addWatcher = useAddWatcher();
  const removeWatcher = useRemoveWatcher();
  const currentUserId = useAuthStore((s) => s.user?.id);

  // Derive pending state from mutations — no redundant local isSaving state
  const isSaving = addWatcher.isPending || removeWatcher.isPending;

  const { data: clinicians = [], isLoading } = useQuery({
    queryKey: ['client-clinicians', clientId],
    queryFn: () => clientService.getClientClinicians(clientId as string),
    enabled: isOpen && !!clientId,
    staleTime: 60_000,
  });

  const currentWatcherIds = useMemo(
    () => new Set(currentWatchers.map((w) => w.userId)),
    [currentWatchers],
  );

  const clinicianIds = useMemo(
    () => new Set(clinicians.map((c) => c.userId)),
    [clinicians],
  );

  const members = useMemo(() => {
    // The assignee already follows the task by assignment, and the current user can't
    // watch their own task, so never offer either as a watcher candidate.
    const exclude = new Set([assigneeUserId, currentUserId].filter(Boolean) as string[]);

    // Matches name, email, location/center and role (ADO 406).
    const filteredClinicians = debouncedSearch
      ? clinicians.filter((c) => matchesPerson(c, debouncedSearch))
      : clinicians;

    // Prepend any current watchers that aren't in the client's clinician list (e.g. a
    // watcher added before a clinician reassignment) so they remain visible/removable.
    // Only shown when not searching, matching the clinician list's unfiltered state.
    const orphanedWatchers = debouncedSearch
      ? []
      : currentWatchers
          .filter((w) => !clinicianIds.has(w.userId))
          .map((w) => ({
            userId: w.userId,
            firstName: w.firstName,
            lastName: w.lastName,
            email: '',
            memberId: '',
            assignedAt: w.createdAt,
            centerName: null,
            roleName: null,
            roleNames: [],
          }));

    return [...orphanedWatchers, ...filteredClinicians].filter((m) => !exclude.has(m.userId));
  }, [clinicians, clinicianIds, currentWatchers, debouncedSearch, assigneeUserId, currentUserId]);

  const handleToggle = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    const toAdd = [...selected].filter((id) => !currentWatcherIds.has(id));
    const toRemove = [...currentWatcherIds].filter((id) => !selected.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      onClose();
      return;
    }

    setSaveError(null);

    try {
      await Promise.all([
        ...toAdd.map((userId) => addWatcher.mutateAsync({ taskId, userId })),
        ...toRemove.map((userId) => removeWatcher.mutateAsync({ taskId, userId })),
      ]);
      onClose();
    } catch {
      setSaveError('Failed to update watchers. Please try again.');
    }
  };

  const footer = (
    <FooterRow>
      {saveError && <ErrorText role="alert">{saveError}</ErrorText>}
      <FooterSpacer />
      <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={isSaving}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        type="button"
        onClick={handleSave}
        isLoading={isSaving}
        disabled={isSaving}
      >
        Save
      </Button>
    </FooterRow>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage watchers"
      size="md"
      footer={footer}
      zIndex={WATCHER_PICKER_Z_INDEX}
    >
      <SearchBox>
        <Search aria-hidden="true" />
        <SearchInput
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, location, or role..."
          aria-label="Search clinicians by name, location, or role"
        />
      </SearchBox>

      {isLoading ? (
        <StatusText>Loading clinicians...</StatusText>
      ) : members.length === 0 ? (
        <StatusText>
          {debouncedSearch ? 'No clinicians match your search.' : 'No clinicians assigned to this client.'}
        </StatusText>
      ) : (
        <MemberList>
          {members.map((member) => (
            <MemberRow key={member.userId} onClick={() => handleToggle(member.userId)}>
              <MemberCheckbox
                type="checkbox"
                checked={selected.has(member.userId)}
                onChange={() => handleToggle(member.userId)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`${member.firstName} ${member.lastName}`}
              />
              <Avatar firstName={member.firstName} lastName={member.lastName} size="sm" />
              <MemberInfo>
                <MemberName>
                  {member.firstName} {member.lastName}
                </MemberName>
                {(() => {
                  // Orphaned watchers have no member record, so no location/role to
                  // report — show nothing rather than a misleading "No location".
                  const meta = formatPersonMeta({
                    centerName: member.centerName,
                    roleName: member.roleName,
                    locationFallback: member.memberId ? 'No location' : undefined,
                  });
                  return meta ? <MemberMeta>{meta}</MemberMeta> : null;
                })()}
              </MemberInfo>
            </MemberRow>
          ))}
        </MemberList>
      )}
    </Modal>
  );
}

// ============================================================================
// Public component — gates rendering so inner content mounts fresh on open
// ============================================================================

export function WatcherPickerModal(props: WatcherPickerModalProps) {
  if (!props.isOpen) return null;
  // key=taskId ensures a fresh mount if the modal is opened for a different
  // task without unmounting (e.g. rapid task switching in a detail panel).
  return <WatcherPickerContent key={props.taskId} {...props} />;
}

export default WatcherPickerModal;
